/* eslint-disable */
import _m0 from "protobufjs/minimal";
import { PosYawPb } from "../../gen/body/pos_yaw";
import { Vector3fPb } from "../../gen/eigen_lcm/Vector3f";
import { Geofence } from "../../util/geofence/geofence";
import { PathfinderArtifactMetadata } from "../../util/pathfinder/pathfinder_metadata";
import { ReturnSettings } from "../../vehicle/infrastructure/flight_deck/return_settings";
import { PositionXY, PositionZ } from "../../vehicle/skills/waypoints";

export const protobufPackage = "cloud_config";

export enum GeofenceLayer {
  OFF = 0,
  /** BASE - Geofences applied in all modes of flight. */
  BASE = 1,
  /** AUTONOMOUS - Geofences applied in autonomous modes of flight (Pathfinder). */
  AUTONOMOUS = 2,
  /** WARNING - Geofences that are not enforced, but trigger a warning. */
  WARNING = 3,
  UNRECOGNIZED = -1,
}

export function geofenceLayerFromJSON(object: any): GeofenceLayer {
  switch (object) {
    case 0:
    case "OFF":
      return GeofenceLayer.OFF;
    case 1:
    case "BASE":
      return GeofenceLayer.BASE;
    case 2:
    case "AUTONOMOUS":
      return GeofenceLayer.AUTONOMOUS;
    case 3:
    case "WARNING":
      return GeofenceLayer.WARNING;
    case -1:
    case "UNRECOGNIZED":
    default:
      return GeofenceLayer.UNRECOGNIZED;
  }
}

export function geofenceLayerToJSON(object: GeofenceLayer): string {
  switch (object) {
    case GeofenceLayer.OFF:
      return "OFF";
    case GeofenceLayer.BASE:
      return "BASE";
    case GeofenceLayer.AUTONOMOUS:
      return "AUTONOMOUS";
    case GeofenceLayer.WARNING:
      return "WARNING";
    case GeofenceLayer.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** Information about the site to which this drone belongs. */
export interface SiteInfo {
  /**
   * The transforms between the site frame its nav cam pose graphs, keyed by NCPG file IDs. When a
   * mission with waypoints in the SITE frames is being run, the corresponding NCPG file ID will be
   * used to select a transform from this map as the source of truth for mapping waypoints from the
   * SITE frame to the NAV_CAM_POSE_GRAPH_FRAME.
   *
   * NOTE(duncan): The UUID is a string here because uuid_util.Uuid cannot be used as the map key
   * and there is not an int128 type. This string must parseable as a 32 character UUID.
   */
  siteTNcpgByNcpgId: { [key: string]: PosYawPb };
  /**
   * The estimate of the transform from its site frame to this drone's takeoff point. This transform
   * is used in two ways:
   * 1. Provide an estimate to the Localizer so that it may select the right vertices to localize
   *    against.
   * 2. When a persistent global graph is sued, to seed default poses of nodes added to the global
   *    graph that correspond to waypoints (i.e., this transform is used in deriving the message
   *    published on `NCPG_WAYPOINTS_REQUEST_PB`).
   *
   * Note that if the drone
   * is taking off at the same point as the origin of a pose graph, then this transform will be
   * the same as one of those in site_T_ncpg_by_ncpg_id. Having this transform specified separately
   * allows for the drone to take off at a different point than the origin of a pose graph and still
   * localize.
   */
  estimateSiteTNavTakeoff:
    | PosYawPb
    | undefined;
  /**
   * The `Geofence` type has a frame for the polygon and the height limits, so should be interpreted
   * accordingly. Our intention as of 2023/05/31 is to store the polygon in GPS frame and the
   * height limits in the NAV_TAKEOFF frame.
   * See https://skydio.slack.com/archives/C052F1DCTQA/p1685133933983159
   */
  geofence:
    | Geofence
    | undefined;
  /** The site's name. */
  name: string;
  /** Lat / Lon / Alt of the site. */
  gpsOrigin:
    | Vector3fPb
    | undefined;
  /** List of safe landing points */
  safeLandingPoints: SafeLandingPoint[];
  /**
   * The ID of the pathfinder artifact that is associated with this site, accounting for surface
   * data. This is required for Pathfinder navigation.
   * Used during pathfinder Alpha and Beta. Pathfinder GA reads the pathfinder file ID from the
   * pathfinder metadata
   */
  pathfinderDataFileId: string;
  /**
   * Pathfinder metadata for this vehicle. Metadata includes artifact version info, settings, and
   * the organization file uuid to download the artifacts
   */
  pathfinderMetadata:
    | PathfinderArtifactMetadata
    | undefined;
  /**
   * The layer assigned to each geofence file.
   * Currently only BASE and AUTONOMOUS layers are used.
   * Ex: site geofence should always be in BASE, pathfinder artifacts in AUTONOMOUS.
   * NOTE(yves): to be deprecated soon, replaced with file_to_geofence_enforcement
   */
  fileToGeofenceLayer: { [key: string]: GeofenceLayer };
  /**
   * The initial enforcement mode and lock status for each geofence layer.
   * The enforcement mode (OFF/BASE/AUTONOMOUS) of an unlocked layer can be changed mid-flight
   */
  fileToGeofenceEnforcement: { [key: string]: GeofenceEnforcement };
}

export interface SiteInfo_SiteTNcpgByNcpgIdEntry {
  key: string;
  value: PosYawPb | undefined;
}

export interface SiteInfo_FileToGeofenceLayerEntry {
  key: string;
  value: GeofenceLayer;
}

export interface SiteInfo_FileToGeofenceEnforcementEntry {
  key: string;
  value: GeofenceEnforcement | undefined;
}

export interface SafeLandingPoint {
  /** Unique identifier */
  uuid: string;
  /** Framed xy position of the safe landing point */
  position:
    | PositionXY
    | undefined;
  /** Framed z position of the safe landing point */
  height:
    | PositionZ
    | undefined;
  /** Return settings */
  returnSettings: ReturnSettings | undefined;
}

export interface GeofenceLock {
}

export enum GeofenceLock_Enum {
  /** UNLOCKED - Unlocked geofence layers can change enforcement mode mid-flight */
  UNLOCKED = 0,
  /** LOCKED - Locked geofence layers can not change enforcement mode mid-flight */
  LOCKED = 1,
  UNRECOGNIZED = -1,
}

export function geofenceLock_EnumFromJSON(object: any): GeofenceLock_Enum {
  switch (object) {
    case 0:
    case "UNLOCKED":
      return GeofenceLock_Enum.UNLOCKED;
    case 1:
    case "LOCKED":
      return GeofenceLock_Enum.LOCKED;
    case -1:
    case "UNRECOGNIZED":
    default:
      return GeofenceLock_Enum.UNRECOGNIZED;
  }
}

export function geofenceLock_EnumToJSON(object: GeofenceLock_Enum): string {
  switch (object) {
    case GeofenceLock_Enum.UNLOCKED:
      return "UNLOCKED";
    case GeofenceLock_Enum.LOCKED:
      return "LOCKED";
    case GeofenceLock_Enum.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** The initial enforcement state of a geofence layer */
export interface GeofenceEnforcement {
  /** Initial enforcement mode */
  enforcementMode: GeofenceLayer;
  /** Whether or not the geofence enforcement mode is allowed to be modified mid-flight */
  lock: GeofenceLock_Enum;
}

function createBaseSiteInfo(): SiteInfo {
  return {
    siteTNcpgByNcpgId: {},
    estimateSiteTNavTakeoff: undefined,
    geofence: undefined,
    name: "",
    gpsOrigin: undefined,
    safeLandingPoints: [],
    pathfinderDataFileId: "",
    pathfinderMetadata: undefined,
    fileToGeofenceLayer: {},
    fileToGeofenceEnforcement: {},
  };
}

export const SiteInfo = {
  encode(message: SiteInfo, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    Object.entries(message.siteTNcpgByNcpgId).forEach(([key, value]) => {
      SiteInfo_SiteTNcpgByNcpgIdEntry.encode({ key: key as any, value }, writer.uint32(10).fork()).ldelim();
    });
    if (message.estimateSiteTNavTakeoff !== undefined) {
      PosYawPb.encode(message.estimateSiteTNavTakeoff, writer.uint32(18).fork()).ldelim();
    }
    if (message.geofence !== undefined) {
      Geofence.encode(message.geofence, writer.uint32(26).fork()).ldelim();
    }
    if (message.name !== "") {
      writer.uint32(34).string(message.name);
    }
    if (message.gpsOrigin !== undefined) {
      Vector3fPb.encode(message.gpsOrigin, writer.uint32(42).fork()).ldelim();
    }
    for (const v of message.safeLandingPoints) {
      SafeLandingPoint.encode(v!, writer.uint32(50).fork()).ldelim();
    }
    if (message.pathfinderDataFileId !== "") {
      writer.uint32(58).string(message.pathfinderDataFileId);
    }
    if (message.pathfinderMetadata !== undefined) {
      PathfinderArtifactMetadata.encode(message.pathfinderMetadata, writer.uint32(74).fork()).ldelim();
    }
    Object.entries(message.fileToGeofenceLayer).forEach(([key, value]) => {
      SiteInfo_FileToGeofenceLayerEntry.encode({ key: key as any, value }, writer.uint32(66).fork()).ldelim();
    });
    Object.entries(message.fileToGeofenceEnforcement).forEach(([key, value]) => {
      SiteInfo_FileToGeofenceEnforcementEntry.encode({ key: key as any, value }, writer.uint32(82).fork()).ldelim();
    });
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SiteInfo {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSiteInfo();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          const entry1 = SiteInfo_SiteTNcpgByNcpgIdEntry.decode(reader, reader.uint32());
          if (entry1.value !== undefined) {
            message.siteTNcpgByNcpgId[entry1.key] = entry1.value;
          }
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.estimateSiteTNavTakeoff = PosYawPb.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.geofence = Geofence.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.name = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.gpsOrigin = Vector3fPb.decode(reader, reader.uint32());
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.safeLandingPoints.push(SafeLandingPoint.decode(reader, reader.uint32()));
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.pathfinderDataFileId = reader.string();
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.pathfinderMetadata = PathfinderArtifactMetadata.decode(reader, reader.uint32());
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          const entry8 = SiteInfo_FileToGeofenceLayerEntry.decode(reader, reader.uint32());
          if (entry8.value !== undefined) {
            message.fileToGeofenceLayer[entry8.key] = entry8.value;
          }
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          const entry10 = SiteInfo_FileToGeofenceEnforcementEntry.decode(reader, reader.uint32());
          if (entry10.value !== undefined) {
            message.fileToGeofenceEnforcement[entry10.key] = entry10.value;
          }
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SiteInfo {
    return {
      siteTNcpgByNcpgId: isObject(object.siteTNcpgByNcpgId)
        ? Object.entries(object.siteTNcpgByNcpgId).reduce<{ [key: string]: PosYawPb }>((acc, [key, value]) => {
          acc[key] = PosYawPb.fromJSON(value);
          return acc;
        }, {})
        : {},
      estimateSiteTNavTakeoff: isSet(object.estimateSiteTNavTakeoff)
        ? PosYawPb.fromJSON(object.estimateSiteTNavTakeoff)
        : undefined,
      geofence: isSet(object.geofence) ? Geofence.fromJSON(object.geofence) : undefined,
      name: isSet(object.name) ? globalThis.String(object.name) : "",
      gpsOrigin: isSet(object.gpsOrigin) ? Vector3fPb.fromJSON(object.gpsOrigin) : undefined,
      safeLandingPoints: globalThis.Array.isArray(object?.safeLandingPoints)
        ? object.safeLandingPoints.map((e: any) => SafeLandingPoint.fromJSON(e))
        : [],
      pathfinderDataFileId: isSet(object.pathfinderDataFileId) ? globalThis.String(object.pathfinderDataFileId) : "",
      pathfinderMetadata: isSet(object.pathfinderMetadata)
        ? PathfinderArtifactMetadata.fromJSON(object.pathfinderMetadata)
        : undefined,
      fileToGeofenceLayer: isObject(object.fileToGeofenceLayer)
        ? Object.entries(object.fileToGeofenceLayer).reduce<{ [key: string]: GeofenceLayer }>((acc, [key, value]) => {
          acc[key] = geofenceLayerFromJSON(value);
          return acc;
        }, {})
        : {},
      fileToGeofenceEnforcement: isObject(object.fileToGeofenceEnforcement)
        ? Object.entries(object.fileToGeofenceEnforcement).reduce<{ [key: string]: GeofenceEnforcement }>(
          (acc, [key, value]) => {
            acc[key] = GeofenceEnforcement.fromJSON(value);
            return acc;
          },
          {},
        )
        : {},
    };
  },

  toJSON(message: SiteInfo): unknown {
    const obj: any = {};
    if (message.siteTNcpgByNcpgId) {
      const entries = Object.entries(message.siteTNcpgByNcpgId);
      if (entries.length > 0) {
        obj.siteTNcpgByNcpgId = {};
        entries.forEach(([k, v]) => {
          obj.siteTNcpgByNcpgId[k] = PosYawPb.toJSON(v);
        });
      }
    }
    if (message.estimateSiteTNavTakeoff !== undefined) {
      obj.estimateSiteTNavTakeoff = PosYawPb.toJSON(message.estimateSiteTNavTakeoff);
    }
    if (message.geofence !== undefined) {
      obj.geofence = Geofence.toJSON(message.geofence);
    }
    if (message.name !== "") {
      obj.name = message.name;
    }
    if (message.gpsOrigin !== undefined) {
      obj.gpsOrigin = Vector3fPb.toJSON(message.gpsOrigin);
    }
    if (message.safeLandingPoints?.length) {
      obj.safeLandingPoints = message.safeLandingPoints.map((e) => SafeLandingPoint.toJSON(e));
    }
    if (message.pathfinderDataFileId !== "") {
      obj.pathfinderDataFileId = message.pathfinderDataFileId;
    }
    if (message.pathfinderMetadata !== undefined) {
      obj.pathfinderMetadata = PathfinderArtifactMetadata.toJSON(message.pathfinderMetadata);
    }
    if (message.fileToGeofenceLayer) {
      const entries = Object.entries(message.fileToGeofenceLayer);
      if (entries.length > 0) {
        obj.fileToGeofenceLayer = {};
        entries.forEach(([k, v]) => {
          obj.fileToGeofenceLayer[k] = geofenceLayerToJSON(v);
        });
      }
    }
    if (message.fileToGeofenceEnforcement) {
      const entries = Object.entries(message.fileToGeofenceEnforcement);
      if (entries.length > 0) {
        obj.fileToGeofenceEnforcement = {};
        entries.forEach(([k, v]) => {
          obj.fileToGeofenceEnforcement[k] = GeofenceEnforcement.toJSON(v);
        });
      }
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SiteInfo>, I>>(base?: I): SiteInfo {
    return SiteInfo.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SiteInfo>, I>>(object: I): SiteInfo {
    const message = createBaseSiteInfo();
    message.siteTNcpgByNcpgId = Object.entries(object.siteTNcpgByNcpgId ?? {}).reduce<{ [key: string]: PosYawPb }>(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = PosYawPb.fromPartial(value);
        }
        return acc;
      },
      {},
    );
    message.estimateSiteTNavTakeoff =
      (object.estimateSiteTNavTakeoff !== undefined && object.estimateSiteTNavTakeoff !== null)
        ? PosYawPb.fromPartial(object.estimateSiteTNavTakeoff)
        : undefined;
    message.geofence = (object.geofence !== undefined && object.geofence !== null)
      ? Geofence.fromPartial(object.geofence)
      : undefined;
    message.name = object.name ?? "";
    message.gpsOrigin = (object.gpsOrigin !== undefined && object.gpsOrigin !== null)
      ? Vector3fPb.fromPartial(object.gpsOrigin)
      : undefined;
    message.safeLandingPoints = object.safeLandingPoints?.map((e) => SafeLandingPoint.fromPartial(e)) || [];
    message.pathfinderDataFileId = object.pathfinderDataFileId ?? "";
    message.pathfinderMetadata = (object.pathfinderMetadata !== undefined && object.pathfinderMetadata !== null)
      ? PathfinderArtifactMetadata.fromPartial(object.pathfinderMetadata)
      : undefined;
    message.fileToGeofenceLayer = Object.entries(object.fileToGeofenceLayer ?? {}).reduce<
      { [key: string]: GeofenceLayer }
    >((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value as GeofenceLayer;
      }
      return acc;
    }, {});
    message.fileToGeofenceEnforcement = Object.entries(object.fileToGeofenceEnforcement ?? {}).reduce<
      { [key: string]: GeofenceEnforcement }
    >((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = GeofenceEnforcement.fromPartial(value);
      }
      return acc;
    }, {});
    return message;
  },
};

function createBaseSiteInfo_SiteTNcpgByNcpgIdEntry(): SiteInfo_SiteTNcpgByNcpgIdEntry {
  return { key: "", value: undefined };
}

export const SiteInfo_SiteTNcpgByNcpgIdEntry = {
  encode(message: SiteInfo_SiteTNcpgByNcpgIdEntry, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== undefined) {
      PosYawPb.encode(message.value, writer.uint32(18).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SiteInfo_SiteTNcpgByNcpgIdEntry {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSiteInfo_SiteTNcpgByNcpgIdEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.value = PosYawPb.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SiteInfo_SiteTNcpgByNcpgIdEntry {
    return {
      key: isSet(object.key) ? globalThis.String(object.key) : "",
      value: isSet(object.value) ? PosYawPb.fromJSON(object.value) : undefined,
    };
  },

  toJSON(message: SiteInfo_SiteTNcpgByNcpgIdEntry): unknown {
    const obj: any = {};
    if (message.key !== "") {
      obj.key = message.key;
    }
    if (message.value !== undefined) {
      obj.value = PosYawPb.toJSON(message.value);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SiteInfo_SiteTNcpgByNcpgIdEntry>, I>>(base?: I): SiteInfo_SiteTNcpgByNcpgIdEntry {
    return SiteInfo_SiteTNcpgByNcpgIdEntry.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SiteInfo_SiteTNcpgByNcpgIdEntry>, I>>(
    object: I,
  ): SiteInfo_SiteTNcpgByNcpgIdEntry {
    const message = createBaseSiteInfo_SiteTNcpgByNcpgIdEntry();
    message.key = object.key ?? "";
    message.value = (object.value !== undefined && object.value !== null)
      ? PosYawPb.fromPartial(object.value)
      : undefined;
    return message;
  },
};

function createBaseSiteInfo_FileToGeofenceLayerEntry(): SiteInfo_FileToGeofenceLayerEntry {
  return { key: "", value: 0 };
}

export const SiteInfo_FileToGeofenceLayerEntry = {
  encode(message: SiteInfo_FileToGeofenceLayerEntry, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== 0) {
      writer.uint32(16).int32(message.value);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SiteInfo_FileToGeofenceLayerEntry {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSiteInfo_FileToGeofenceLayerEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.value = reader.int32() as any;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SiteInfo_FileToGeofenceLayerEntry {
    return {
      key: isSet(object.key) ? globalThis.String(object.key) : "",
      value: isSet(object.value) ? geofenceLayerFromJSON(object.value) : 0,
    };
  },

  toJSON(message: SiteInfo_FileToGeofenceLayerEntry): unknown {
    const obj: any = {};
    if (message.key !== "") {
      obj.key = message.key;
    }
    if (message.value !== 0) {
      obj.value = geofenceLayerToJSON(message.value);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SiteInfo_FileToGeofenceLayerEntry>, I>>(
    base?: I,
  ): SiteInfo_FileToGeofenceLayerEntry {
    return SiteInfo_FileToGeofenceLayerEntry.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SiteInfo_FileToGeofenceLayerEntry>, I>>(
    object: I,
  ): SiteInfo_FileToGeofenceLayerEntry {
    const message = createBaseSiteInfo_FileToGeofenceLayerEntry();
    message.key = object.key ?? "";
    message.value = object.value ?? 0;
    return message;
  },
};

function createBaseSiteInfo_FileToGeofenceEnforcementEntry(): SiteInfo_FileToGeofenceEnforcementEntry {
  return { key: "", value: undefined };
}

export const SiteInfo_FileToGeofenceEnforcementEntry = {
  encode(message: SiteInfo_FileToGeofenceEnforcementEntry, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== undefined) {
      GeofenceEnforcement.encode(message.value, writer.uint32(18).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SiteInfo_FileToGeofenceEnforcementEntry {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSiteInfo_FileToGeofenceEnforcementEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.value = GeofenceEnforcement.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SiteInfo_FileToGeofenceEnforcementEntry {
    return {
      key: isSet(object.key) ? globalThis.String(object.key) : "",
      value: isSet(object.value) ? GeofenceEnforcement.fromJSON(object.value) : undefined,
    };
  },

  toJSON(message: SiteInfo_FileToGeofenceEnforcementEntry): unknown {
    const obj: any = {};
    if (message.key !== "") {
      obj.key = message.key;
    }
    if (message.value !== undefined) {
      obj.value = GeofenceEnforcement.toJSON(message.value);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SiteInfo_FileToGeofenceEnforcementEntry>, I>>(
    base?: I,
  ): SiteInfo_FileToGeofenceEnforcementEntry {
    return SiteInfo_FileToGeofenceEnforcementEntry.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SiteInfo_FileToGeofenceEnforcementEntry>, I>>(
    object: I,
  ): SiteInfo_FileToGeofenceEnforcementEntry {
    const message = createBaseSiteInfo_FileToGeofenceEnforcementEntry();
    message.key = object.key ?? "";
    message.value = (object.value !== undefined && object.value !== null)
      ? GeofenceEnforcement.fromPartial(object.value)
      : undefined;
    return message;
  },
};

function createBaseSafeLandingPoint(): SafeLandingPoint {
  return { uuid: "", position: undefined, height: undefined, returnSettings: undefined };
}

export const SafeLandingPoint = {
  encode(message: SafeLandingPoint, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.uuid !== "") {
      writer.uint32(10).string(message.uuid);
    }
    if (message.position !== undefined) {
      PositionXY.encode(message.position, writer.uint32(18).fork()).ldelim();
    }
    if (message.height !== undefined) {
      PositionZ.encode(message.height, writer.uint32(26).fork()).ldelim();
    }
    if (message.returnSettings !== undefined) {
      ReturnSettings.encode(message.returnSettings, writer.uint32(34).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SafeLandingPoint {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSafeLandingPoint();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.uuid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.position = PositionXY.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.height = PositionZ.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.returnSettings = ReturnSettings.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SafeLandingPoint {
    return {
      uuid: isSet(object.uuid) ? globalThis.String(object.uuid) : "",
      position: isSet(object.position) ? PositionXY.fromJSON(object.position) : undefined,
      height: isSet(object.height) ? PositionZ.fromJSON(object.height) : undefined,
      returnSettings: isSet(object.returnSettings) ? ReturnSettings.fromJSON(object.returnSettings) : undefined,
    };
  },

  toJSON(message: SafeLandingPoint): unknown {
    const obj: any = {};
    if (message.uuid !== "") {
      obj.uuid = message.uuid;
    }
    if (message.position !== undefined) {
      obj.position = PositionXY.toJSON(message.position);
    }
    if (message.height !== undefined) {
      obj.height = PositionZ.toJSON(message.height);
    }
    if (message.returnSettings !== undefined) {
      obj.returnSettings = ReturnSettings.toJSON(message.returnSettings);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SafeLandingPoint>, I>>(base?: I): SafeLandingPoint {
    return SafeLandingPoint.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SafeLandingPoint>, I>>(object: I): SafeLandingPoint {
    const message = createBaseSafeLandingPoint();
    message.uuid = object.uuid ?? "";
    message.position = (object.position !== undefined && object.position !== null)
      ? PositionXY.fromPartial(object.position)
      : undefined;
    message.height = (object.height !== undefined && object.height !== null)
      ? PositionZ.fromPartial(object.height)
      : undefined;
    message.returnSettings = (object.returnSettings !== undefined && object.returnSettings !== null)
      ? ReturnSettings.fromPartial(object.returnSettings)
      : undefined;
    return message;
  },
};

function createBaseGeofenceLock(): GeofenceLock {
  return {};
}

export const GeofenceLock = {
  encode(_: GeofenceLock, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): GeofenceLock {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGeofenceLock();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(_: any): GeofenceLock {
    return {};
  },

  toJSON(_: GeofenceLock): unknown {
    const obj: any = {};
    return obj;
  },

  create<I extends Exact<DeepPartial<GeofenceLock>, I>>(base?: I): GeofenceLock {
    return GeofenceLock.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GeofenceLock>, I>>(_: I): GeofenceLock {
    const message = createBaseGeofenceLock();
    return message;
  },
};

function createBaseGeofenceEnforcement(): GeofenceEnforcement {
  return { enforcementMode: 0, lock: 0 };
}

export const GeofenceEnforcement = {
  encode(message: GeofenceEnforcement, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.enforcementMode !== 0) {
      writer.uint32(8).int32(message.enforcementMode);
    }
    if (message.lock !== 0) {
      writer.uint32(16).int32(message.lock);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): GeofenceEnforcement {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGeofenceEnforcement();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.enforcementMode = reader.int32() as any;
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.lock = reader.int32() as any;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): GeofenceEnforcement {
    return {
      enforcementMode: isSet(object.enforcementMode) ? geofenceLayerFromJSON(object.enforcementMode) : 0,
      lock: isSet(object.lock) ? geofenceLock_EnumFromJSON(object.lock) : 0,
    };
  },

  toJSON(message: GeofenceEnforcement): unknown {
    const obj: any = {};
    if (message.enforcementMode !== 0) {
      obj.enforcementMode = geofenceLayerToJSON(message.enforcementMode);
    }
    if (message.lock !== 0) {
      obj.lock = geofenceLock_EnumToJSON(message.lock);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GeofenceEnforcement>, I>>(base?: I): GeofenceEnforcement {
    return GeofenceEnforcement.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<GeofenceEnforcement>, I>>(object: I): GeofenceEnforcement {
    const message = createBaseGeofenceEnforcement();
    message.enforcementMode = object.enforcementMode ?? 0;
    message.lock = object.lock ?? 0;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function isObject(value: any): boolean {
  return typeof value === "object" && value !== null;
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
